import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/config';

// Try to import recharts - if not available, charts will show a message
let LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Brush;
try {
  const recharts = require('recharts');
  LineChart = recharts.LineChart;
  Line = recharts.Line;
  XAxis = recharts.XAxis;
  YAxis = recharts.YAxis;
  CartesianGrid = recharts.CartesianGrid;
  Tooltip = recharts.Tooltip;
  ResponsiveContainer = recharts.ResponsiveContainer;
  Brush = recharts.Brush;
} catch (e) {
  // recharts not installed
}

// Format time for chart axis
function formatChartTime(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Format relative time
function formatRelativeTime(dateStr) {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return 'Invalid';

  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);

  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffMin < 60) return `${diffMin}m ago`;
  return `${diffHour}h ${diffMin % 60}m ago`;
}

// Format full datetime
function formatDateTime(dateStr) {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return 'Invalid';
  return date.toLocaleString();
}

// Status colors
const STATUS_COLORS = {
  ok: { bg: '#1b5e20', text: '#fff' },
  attention: { bg: '#f57c00', text: '#fff' },
  down: { bg: '#c62828', text: '#fff' },
};

// Blank reason colors (v1.2.5+)
const BLANK_REASON_COLORS = {
  NONE: { bg: '#1b5e20', text: '#fff', label: 'Healthy' },
  EMPTY: { bg: '#616161', text: '#fff', label: 'No Flights' },
  FILTERED_OUT: { bg: '#f57c00', text: '#fff', label: 'Filtered' },
  BROKEN_UI: { bg: '#c62828', text: '#fff', label: 'Broken UI' },
  NETWORK_STALE: { bg: '#fbc02d', text: '#000', label: 'Stale' },
  WEBVIEW_FREEZE: { bg: '#7b1fa2', text: '#fff', label: 'Frozen' },
};

// Read current user role from stored auth
function getUserRole() {
  try {
    const authData = localStorage.getItem('auth');
    return authData ? JSON.parse(authData).data?.role : null;
  } catch {
    return null;
  }
}

export default function HealthDetailDrawer({ open, device, timeRange, onClose }) {
  const isAdmin = getUserRole() === 'admin';
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showRawJson, setShowRawJson] = useState(false);

  // Restart/reload command states
  const [confirmAction, setConfirmAction] = useState(null); // 'restart' | 'reload' | null
  const [commandLoading, setCommandLoading] = useState(false);
  const [commandResult, setCommandResult] = useState(null); // { success, message }

  // Fetch history when device changes
  const fetchHistory = useCallback(async () => {
    if (!device?.deviceId || !timeRange) return;

    setLoading(true);
    setError(null);

    try {
      const params = {
        from: timeRange.from,
        to: timeRange.to,
        limit: 500,
      };

      const response = await axios.get(
        `${API_BASE_URL}/api/admin/heartbeats/history/${device.deviceId}`,
        { params }
      );

      if (response.data?.success) {
        // Reverse to chronological order for charts
        setHistory((response.data.history || []).reverse());
      } else {
        throw new Error(response.data?.message || 'Failed to fetch history');
      }
    } catch (err) {
      console.error('Error fetching device history:', err);
      setError(err.message);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [device?.deviceId, timeRange]);

  useEffect(() => {
    if (open && device) {
      fetchHistory();
    }
  }, [open, device, fetchHistory]);

  // Send restart or reload command to device
  const sendCommand = useCallback(async (commandType) => {
    if (!device?.deviceId) return;

    setCommandLoading(true);
    setCommandResult(null);

    try {
      const endpoint = commandType === 'restart'
        ? `${API_BASE_URL}/api/heartbeat/devices/${device.deviceId}/restart`
        : `${API_BASE_URL}/api/heartbeat/devices/${device.deviceId}/reload`;

      const response = await axios.post(endpoint, {
        reason: 'admin_dashboard_request'
      });

      if (response.data?.success) {
        setCommandResult({
          success: true,
          message: `${commandType === 'restart' ? 'Restart' : 'Reload'} command queued successfully. Device will execute on next heartbeat (within 60 seconds).`
        });
      } else {
        throw new Error(response.data?.message || 'Unknown error');
      }
    } catch (err) {
      console.error(`Error sending ${commandType} command:`, err);
      setCommandResult({
        success: false,
        message: err.response?.data?.message || err.message || 'Failed to send command'
      });
    } finally {
      setCommandLoading(false);
      setConfirmAction(null);
    }
  }, [device?.deviceId]);

  // Handle confirm action
  const handleConfirmAction = () => {
    if (confirmAction) {
      sendCommand(confirmAction);
    }
  };

  // If not open, render nothing
  if (!open) return null;

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'flex-end',
    },
    drawer: {
      width: '700px',
      maxWidth: '90vw',
      height: '100%',
      backgroundColor: '#0a0a0a',
      color: '#fff',
      boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.5)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    header: {
      padding: '20px',
      borderBottom: '1px solid #333',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
    closeButton: {
      background: 'none',
      border: 'none',
      color: '#888',
      fontSize: '24px',
      cursor: 'pointer',
      padding: '0 8px',
    },
    title: {
      fontSize: '20px',
      fontWeight: 'bold',
      marginBottom: '4px',
    },
    subtitle: {
      fontSize: '13px',
      color: '#888',
      fontFamily: 'monospace',
    },
    tabs: {
      display: 'flex',
      borderBottom: '1px solid #333',
      backgroundColor: '#111',
    },
    tab: {
      padding: '12px 20px',
      border: 'none',
      background: 'none',
      color: '#888',
      cursor: 'pointer',
      fontSize: '14px',
      borderBottom: '2px solid transparent',
    },
    tabActive: {
      padding: '12px 20px',
      border: 'none',
      background: 'none',
      color: '#fff',
      cursor: 'pointer',
      fontSize: '14px',
      borderBottom: '2px solid #1976d2',
      fontWeight: 'bold',
    },
    content: {
      flex: 1,
      overflow: 'auto',
      padding: '20px',
    },
    section: {
      marginBottom: '24px',
    },
    sectionTitle: {
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#888',
      textTransform: 'uppercase',
      marginBottom: '12px',
      borderBottom: '1px solid #333',
      paddingBottom: '8px',
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '16px',
    },
    infoItem: {
      backgroundColor: '#1a1a1a',
      padding: '12px',
      borderRadius: '6px',
    },
    infoLabel: {
      fontSize: '11px',
      color: '#888',
      textTransform: 'uppercase',
      marginBottom: '4px',
    },
    infoValue: {
      fontSize: '16px',
      fontWeight: 'bold',
    },
    chartContainer: {
      backgroundColor: '#1a1a1a',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
    },
    chartTitle: {
      fontSize: '13px',
      color: '#888',
      marginBottom: '12px',
    },
    chartPlaceholder: {
      height: '200px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#666',
      fontSize: '14px',
    },
    historyTable: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '12px',
    },
    historyTh: {
      textAlign: 'left',
      padding: '8px',
      borderBottom: '1px solid #333',
      color: '#888',
      fontSize: '11px',
      textTransform: 'uppercase',
    },
    historyTd: {
      padding: '8px',
      borderBottom: '1px solid #222',
    },
    jsonViewer: {
      backgroundColor: '#1a1a1a',
      borderRadius: '6px',
      padding: '16px',
      fontSize: '12px',
      fontFamily: 'monospace',
      overflow: 'auto',
      maxHeight: '400px',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all',
    },
    statusChip: {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold',
      textTransform: 'uppercase',
    },
    button: {
      backgroundColor: '#333',
      color: '#fff',
      border: '1px solid #444',
      borderRadius: '4px',
      padding: '8px 16px',
      cursor: 'pointer',
      fontSize: '13px',
    },
    loading: {
      textAlign: 'center',
      padding: '40px',
      color: '#888',
    },
    error: {
      backgroundColor: '#4a1c1c',
      border: '1px solid #c62828',
      borderRadius: '4px',
      padding: '12px',
      color: '#ffcdd2',
      marginBottom: '16px',
    },
  };

  // Prepare chart data
  const chartData = history.map(h => ({
    time: formatChartTime(h.createdAt),
    timestamp: new Date(h.createdAt).getTime(),
    dataAge: h.dataAgeSeconds || 0,
    reloadCount: h.reloadCount1h || 0,
    jsErrors: h.jsErrorCount5m || 0,
    uptimeMin: h.uptimeMin || 0,
    flightsReceived: h.flightsReceived ?? null,
    flightsDisplayed: h.flightsDisplayed ?? null,
    domDataRowCount: h.domDataRowCount ?? null,
  }));

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          padding: '10px',
          borderRadius: '4px',
        }}>
          <div style={{ color: '#888', fontSize: '12px', marginBottom: '4px' }}>{label}</div>
          {payload.map((entry, index) => (
            <div key={index} style={{ color: entry.color, fontSize: '13px' }}>
              {entry.name}: {entry.value}
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  // Render chart or placeholder
  const renderChart = (dataKey, color, name, yAxisLabel) => {
    if (!LineChart) {
      return (
        <div style={styles.chartPlaceholder}>
          Install recharts for charts: npm install recharts
        </div>
      );
    }

    if (chartData.length < 2) {
      return (
        <div style={styles.chartPlaceholder}>
          Not enough data points for chart
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
          <XAxis
            dataKey="time"
            stroke="#666"
            fontSize={11}
            tickLine={false}
          />
          <YAxis
            stroke="#666"
            fontSize={11}
            tickLine={false}
            label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', fill: '#666', fontSize: 11 }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Brush dataKey="time" height={20} stroke="#1976d2" fill="#1a1a1a" />
          <Line
            type="monotone"
            dataKey={dataKey}
            name={name}
            stroke={color}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const statusConfig = STATUS_COLORS[device?.status] || STATUS_COLORS.ok;

  // Render blank reason chip
  const renderBlankReasonChip = (blankReason) => {
    const config = BLANK_REASON_COLORS[blankReason] || BLANK_REASON_COLORS.NONE;
    return (
      <span style={{
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: '12px',
        backgroundColor: config.bg,
        color: config.text,
        fontSize: '12px',
        fontWeight: 'bold',
      }}>
        {config.label}
      </span>
    );
  };

  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={styles.drawer}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <div style={styles.title}>{device?.screenName || 'Unknown Device'}</div>
            <div style={styles.subtitle} title={`Device ID: ${device?.deviceId}`}>
              {device?.lastIp || device?.deviceId}
            </div>
            <div style={{ marginTop: '8px' }}>
              <span style={{
                ...styles.statusChip,
                backgroundColor: statusConfig.bg,
                color: statusConfig.text,
              }}>
                {device?.status?.toUpperCase()}
              </span>
              {device?.statusReason && (
                <span style={{ marginLeft: '8px', color: '#888', fontSize: '12px' }}>
                  {device.statusReason}
                </span>
              )}
            </div>
          </div>
          <button style={styles.closeButton} onClick={onClose}>&times;</button>
        </div>

        {/* Tabs */}
        <div style={styles.tabs}>
          <button
            style={activeTab === 'overview' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            style={activeTab === 'charts' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('charts')}
          >
            Charts
          </button>
          <button
            style={activeTab === 'history' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('history')}
          >
            History ({history.length})
          </button>
          <button
            style={activeTab === 'raw' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('raw')}
          >
            Raw Data
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {loading && <div style={styles.loading}>Loading history...</div>}
          {error && <div style={styles.error}>{error}</div>}

          {/* Overview Tab */}
          {activeTab === 'overview' && device && (
            <>
              {/* Device Actions Section (restart/reload are admin-only) */}
              {isAdmin && (
              <div style={styles.section}>
                <div style={styles.sectionTitle}>Device Actions</div>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                  <button
                    onClick={() => setConfirmAction('reload')}
                    disabled={commandLoading}
                    style={{
                      ...styles.button,
                      backgroundColor: '#1976d2',
                      borderColor: '#1976d2',
                      opacity: commandLoading ? 0.6 : 1,
                      cursor: commandLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    🔃 Reload WebView
                  </button>
                  <button
                    onClick={() => setConfirmAction('restart')}
                    disabled={commandLoading}
                    style={{
                      ...styles.button,
                      backgroundColor: '#d32f2f',
                      borderColor: '#d32f2f',
                      opacity: commandLoading ? 0.6 : 1,
                      cursor: commandLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    🔄 Restart App
                  </button>
                </div>
                {commandResult && (
                  <div style={{
                    padding: '12px',
                    borderRadius: '6px',
                    backgroundColor: commandResult.success ? '#1b5e20' : '#4a1c1c',
                    border: `1px solid ${commandResult.success ? '#4caf50' : '#c62828'}`,
                    color: commandResult.success ? '#c8e6c9' : '#ffcdd2',
                    fontSize: '13px',
                    marginTop: '8px',
                  }}>
                    {commandResult.success ? '✅ ' : '❌ '}
                    {commandResult.message}
                  </div>
                )}
                <div style={{ fontSize: '11px', color: '#888', marginTop: '8px' }}>
                  Commands are delivered via heartbeat (within 60 seconds).
                  Reload refreshes the web content. Restart kills and relaunches the app.
                </div>
              </div>
              )}

              <div style={styles.section}>
                <div style={styles.sectionTitle}>Current Status</div>
                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Version</div>
                    <div style={styles.infoValue}>{device.version}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>App Type</div>
                    <div style={styles.infoValue}>{device.appType}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>LAN IP</div>
                    <div style={{ ...styles.infoValue, fontFamily: 'monospace', fontSize: '14px' }}>
                      {device.lastIp || '-'}
                    </div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Uptime</div>
                    <div style={styles.infoValue}>{device.latest?.uptimeMin} min</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Render Status</div>
                    <div style={{
                      ...styles.infoValue,
                      color: device.latest?.renderOk ? '#4caf50' : '#f44336'
                    }}>
                      {device.latest?.renderOk ? 'OK' : 'FAILED'}
                    </div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Last Render</div>
                    <div style={styles.infoValue}>
                      {formatRelativeTime(device.latest?.lastRenderAt)}
                    </div>
                    <div style={{ fontSize: '11px', color: '#666' }}>
                      {formatDateTime(device.latest?.lastRenderAt)}
                    </div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Data Age</div>
                    <div style={{
                      ...styles.infoValue,
                      color: device.latest?.dataAgeSeconds > 180 ? '#ff9800' : '#fff'
                    }}>
                      {device.latest?.dataAgeSeconds}s
                    </div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Reloads (1h)</div>
                    <div style={{
                      ...styles.infoValue,
                      color: device.latest?.reloadCount1h >= 3 ? '#ff9800' : '#fff'
                    }}>
                      {device.latest?.reloadCount1h}
                    </div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>JS Errors (5m)</div>
                    <div style={{
                      ...styles.infoValue,
                      color: device.latest?.jsErrorCount5m >= 1 ? '#f44336' : '#fff'
                    }}>
                      {device.latest?.jsErrorCount5m}
                    </div>
                  </div>
                </div>
              </div>

              {/* Self-Healing Status (v1.2.5+) */}
              {(device.latest?.blankReason || device.latest?.flightsReceived !== undefined) && (
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>Self-Healing Status (v1.2.5+)</div>
                  <div style={styles.infoGrid}>
                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>Blank Reason</div>
                      <div style={styles.infoValue}>
                        {device.latest?.blankReason ? (
                          renderBlankReasonChip(device.latest.blankReason)
                        ) : (
                          <span style={{ color: '#4caf50' }}>NONE</span>
                        )}
                      </div>
                    </div>
                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>Is Blank Board</div>
                      <div style={{
                        ...styles.infoValue,
                        color: device.latest?.isBlankBoard ? '#f44336' : '#4caf50'
                      }}>
                        {device.latest?.isBlankBoard ? 'YES' : 'NO'}
                      </div>
                    </div>
                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>Flights Received</div>
                      <div style={styles.infoValue}>
                        {device.latest?.flightsReceived ?? '-'}
                      </div>
                    </div>
                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>Flights Displayed</div>
                      <div style={{
                        ...styles.infoValue,
                        color: (device.latest?.flightsReceived > 0 && device.latest?.flightsDisplayed === 0) ? '#ff9800' : '#fff'
                      }}>
                        {device.latest?.flightsDisplayed ?? '-'}
                      </div>
                    </div>
                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>DOM Data Rows</div>
                      <div style={{
                        ...styles.infoValue,
                        color: (device.latest?.flightsDisplayed > 0 && device.latest?.domDataRowCount === 0) ? '#f44336' : '#fff'
                      }}>
                        {device.latest?.domDataRowCount ?? '-'}
                      </div>
                    </div>
                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>Last Healthy Render</div>
                      <div style={styles.infoValue}>
                        {device.latest?.lastHealthyRenderAt ? (
                          <>
                            {formatRelativeTime(device.latest.lastHealthyRenderAt)}
                            <div style={{ fontSize: '11px', color: '#666' }}>
                              {formatDateTime(device.latest.lastHealthyRenderAt)}
                            </div>
                          </>
                        ) : (
                          <span style={{ color: '#666' }}>-</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div style={styles.section}>
                <div style={styles.sectionTitle}>Trends (Selected Period)</div>
                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Heartbeats Received</div>
                    <div style={styles.infoValue}>{device.heartbeatCount}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Avg Data Age</div>
                    <div style={styles.infoValue}>{device.trends?.avgDataAgeSeconds}s</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Max Data Age</div>
                    <div style={styles.infoValue}>{device.trends?.maxDataAgeSeconds}s</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Render OK %</div>
                    <div style={styles.infoValue}>{device.trends?.renderOkPercentage}%</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Total Reloads</div>
                    <div style={styles.infoValue}>{device.trends?.totalReloads}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Total JS Errors</div>
                    <div style={styles.infoValue}>{device.trends?.totalJsErrors}</div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Charts Tab */}
          {activeTab === 'charts' && (
            <>
              <div style={styles.chartContainer}>
                <div style={styles.chartTitle}>Data Age Over Time (seconds)</div>
                {renderChart('dataAge', '#2196f3', 'Data Age', 'seconds')}
              </div>

              <div style={styles.chartContainer}>
                <div style={styles.chartTitle}>Reload Count (1h window)</div>
                {renderChart('reloadCount', '#ff9800', 'Reloads', 'count')}
              </div>

              <div style={styles.chartContainer}>
                <div style={styles.chartTitle}>JS Errors (5m window)</div>
                {renderChart('jsErrors', '#f44336', 'JS Errors', 'count')}
              </div>

              <div style={styles.chartContainer}>
                <div style={styles.chartTitle}>Uptime (minutes)</div>
                {renderChart('uptimeMin', '#4caf50', 'Uptime', 'minutes')}
              </div>

              {/* Flights Chart (v1.2.5+) - only show if we have flight data */}
              {chartData.some(d => d.flightsReceived !== null) && (
                <div style={styles.chartContainer}>
                  <div style={styles.chartTitle}>Flights Received vs Displayed (v1.2.5+)</div>
                  {!LineChart ? (
                    <div style={styles.chartPlaceholder}>
                      Install recharts for charts: npm install recharts
                    </div>
                  ) : chartData.length < 2 ? (
                    <div style={styles.chartPlaceholder}>
                      Not enough data points for chart
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis
                          dataKey="time"
                          stroke="#666"
                          fontSize={11}
                          tickLine={false}
                        />
                        <YAxis
                          stroke="#666"
                          fontSize={11}
                          tickLine={false}
                          label={{ value: 'flights', angle: -90, position: 'insideLeft', fill: '#666', fontSize: 11 }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Brush dataKey="time" height={20} stroke="#1976d2" fill="#1a1a1a" />
                        <Line
                          type="monotone"
                          dataKey="flightsReceived"
                          name="Received"
                          stroke="#2196f3"
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                          connectNulls
                        />
                        <Line
                          type="monotone"
                          dataKey="flightsDisplayed"
                          name="Displayed"
                          stroke="#4caf50"
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                          connectNulls
                        />
                        <Line
                          type="monotone"
                          dataKey="domDataRowCount"
                          name="DOM Rows"
                          stroke="#ff9800"
                          strokeWidth={1}
                          strokeDasharray="5 5"
                          dot={false}
                          activeDot={{ r: 3 }}
                          connectNulls
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              )}
            </>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                Heartbeat History
                <span style={{ fontWeight: 'normal', marginLeft: '8px' }}>
                  ({history.length} records)
                </span>
              </div>
              {history.length === 0 ? (
                <div style={{ color: '#666', padding: '20px', textAlign: 'center' }}>
                  No heartbeat history found for this period.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={styles.historyTable}>
                    <thead>
                      <tr>
                        <th style={styles.historyTh}>Time</th>
                        <th style={styles.historyTh}>Uptime</th>
                        <th style={styles.historyTh}>Render</th>
                        <th style={styles.historyTh}>Blank</th>
                        <th style={styles.historyTh}>Flights</th>
                        <th style={styles.historyTh}>Data Age</th>
                        <th style={styles.historyTh}>Reloads</th>
                        <th style={styles.historyTh}>JS Err</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.slice().reverse().slice(0, 100).map((h, idx) => {
                        const hasFilterIssue = h.flightsReceived > 0 && h.flightsDisplayed === 0;
                        const hasRenderIssue = h.flightsDisplayed > 0 && h.domDataRowCount === 0;
                        const blankConfig = BLANK_REASON_COLORS[h.blankReason] || BLANK_REASON_COLORS.NONE;

                        return (
                          <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#0a0a0a' : '#111' }}>
                            <td style={styles.historyTd}>
                              <div>{formatRelativeTime(h.createdAt)}</div>
                              <div style={{ fontSize: '10px', color: '#666' }}>
                                {formatDateTime(h.createdAt)}
                              </div>
                            </td>
                            <td style={styles.historyTd}>{h.uptimeMin}m</td>
                            <td style={styles.historyTd}>
                              <span style={{ color: h.renderOk ? '#4caf50' : '#f44336' }}>
                                {h.renderOk ? 'OK' : 'FAIL'}
                              </span>
                            </td>
                            <td style={styles.historyTd}>
                              {h.blankReason && h.blankReason !== 'NONE' ? (
                                <span style={{
                                  padding: '2px 6px',
                                  borderRadius: '8px',
                                  backgroundColor: blankConfig.bg,
                                  color: blankConfig.text,
                                  fontSize: '9px',
                                  fontWeight: 'bold',
                                }}>
                                  {blankConfig.label}
                                </span>
                              ) : (
                                <span style={{ color: '#666' }}>-</span>
                              )}
                            </td>
                            <td style={styles.historyTd}>
                              {h.flightsReceived !== undefined ? (
                                <span style={{
                                  color: hasFilterIssue ? '#ff9800' : hasRenderIssue ? '#f44336' : '#fff',
                                  fontWeight: (hasFilterIssue || hasRenderIssue) ? 'bold' : 'normal',
                                }}>
                                  {h.flightsDisplayed}/{h.flightsReceived}
                                </span>
                              ) : (
                                <span style={{ color: '#666' }}>-</span>
                              )}
                            </td>
                            <td style={{
                              ...styles.historyTd,
                              color: h.dataAgeSeconds > 180 ? '#ff9800' : '#fff'
                            }}>
                              {h.dataAgeSeconds}s
                            </td>
                            <td style={{
                              ...styles.historyTd,
                              color: h.reloadCount1h >= 3 ? '#ff9800' : '#fff'
                            }}>
                              {h.reloadCount1h}
                            </td>
                            <td style={{
                              ...styles.historyTd,
                              color: h.jsErrorCount5m >= 1 ? '#f44336' : '#fff'
                            }}>
                              {h.jsErrorCount5m}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {history.length > 100 && (
                    <div style={{ padding: '12px', color: '#888', textAlign: 'center', fontSize: '12px' }}>
                      Showing latest 100 of {history.length} records
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Raw Data Tab */}
          {activeTab === 'raw' && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                Device Data
                <button
                  style={{ ...styles.button, marginLeft: '12px', fontSize: '11px', padding: '4px 8px' }}
                  onClick={() => setShowRawJson(!showRawJson)}
                >
                  {showRawJson ? 'Show Summary' : 'Show Full JSON'}
                </button>
              </div>

              {showRawJson ? (
                <pre style={styles.jsonViewer}>
                  {JSON.stringify(device, null, 2)}
                </pre>
              ) : (
                <div style={styles.infoGrid}>
                  <div style={{ ...styles.infoItem, gridColumn: '1 / -1' }}>
                    <div style={styles.infoLabel}>Device ID</div>
                    <div style={{ ...styles.infoValue, fontFamily: 'monospace', fontSize: '14px' }}>
                      {device?.deviceId}
                    </div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Screen Name</div>
                    <div style={styles.infoValue}>{device?.screenName}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Version</div>
                    <div style={styles.infoValue}>{device?.version}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>App Type</div>
                    <div style={styles.infoValue}>{device?.appType}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Status</div>
                    <div style={styles.infoValue}>{device?.status}</div>
                  </div>
                  <div style={{ ...styles.infoItem, gridColumn: '1 / -1' }}>
                    <div style={styles.infoLabel}>Status Reason</div>
                    <div style={styles.infoValue}>{device?.statusReason || 'None'}</div>
                  </div>
                </div>
              )}

              {history.length > 0 && (
                <>
                  <div style={{ ...styles.sectionTitle, marginTop: '24px' }}>
                    Latest Raw Heartbeat Payload
                  </div>
                  <pre style={styles.jsonViewer}>
                    {JSON.stringify(history[history.length - 1], null, 2)}
                  </pre>
                </>
              )}
            </div>
          )}
        </div>

        {/* Confirmation Modal */}
        {confirmAction && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            zIndex: 1001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <div style={{
              backgroundColor: '#1a1a2e',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '450px',
              width: '90%',
              textAlign: 'center',
              border: confirmAction === 'restart' ? '2px solid #d32f2f' : '2px solid #1976d2',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                {confirmAction === 'restart' ? '🔄' : '🔃'}
              </div>
              <h2 style={{
                color: '#fff',
                margin: '0 0 16px 0',
                fontSize: '24px',
                fontWeight: 600,
              }}>
                {confirmAction === 'restart' ? 'Restart Device?' : 'Reload WebView?'}
              </h2>
              <p style={{
                color: '#aaa',
                margin: '0 0 8px 0',
                fontSize: '16px',
                lineHeight: 1.5,
              }}>
                <strong>{device?.screenName}</strong>
              </p>
              <p style={{
                color: '#888',
                margin: '0 0 24px 0',
                fontSize: '14px',
                lineHeight: 1.5,
              }}>
                {confirmAction === 'restart'
                  ? 'This will kill the app process and relaunch it. The device will be offline for 5-10 seconds.'
                  : 'This will refresh the web content without restarting the app. Quick way to clear stale UI state.'
                }
              </p>
              <div style={{
                display: 'flex',
                gap: '16px',
                justifyContent: 'center',
              }}>
                <button
                  onClick={() => setConfirmAction(null)}
                  disabled={commandLoading}
                  style={{
                    padding: '12px 32px',
                    fontSize: '16px',
                    fontWeight: 600,
                    backgroundColor: '#333',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: commandLoading ? 'not-allowed' : 'pointer',
                    minWidth: '120px',
                    opacity: commandLoading ? 0.6 : 1,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmAction}
                  disabled={commandLoading}
                  style={{
                    padding: '12px 32px',
                    fontSize: '16px',
                    fontWeight: 600,
                    backgroundColor: confirmAction === 'restart' ? '#d32f2f' : '#1976d2',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: commandLoading ? 'not-allowed' : 'pointer',
                    minWidth: '120px',
                    opacity: commandLoading ? 0.6 : 1,
                  }}
                >
                  {commandLoading ? 'Sending...' : confirmAction === 'restart' ? 'Restart' : 'Reload'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
